import { useCallback, useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import MainTable from "../../../../layouts/Table/MainTable";
import {
  ActionIcon,
  Badge,
  Flex,
  Group,
  Loader,
  Switch,
  Tooltip,
} from "@mantine/core";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { Link, useParams } from "react-router-dom";
import { IconEdit, IconUserCheck, IconUserX } from "@tabler/icons-react";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import {
  disableUser,
  enableUserAccount,
  getOrgUsers,
  getResellerUsers,
  getTeamUsers,
  updateUserResellerTags,
} from "../../../../api/UserAPI";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import { parseDate } from "../../../../functions/dates";
import { notifications } from "@mantine/notifications";
import ResellerPointInput from "../../community/ResellerPointInput";
import DeleteAccount from "../../admin/DeleteAccount";
import { tagsColumn } from "../../../../layouts/Table/columns/tagsColumn";
import { formatPhone } from "../../../../functions/utilFunctions";
import UserSubscriptionCell from "./UserSubscriptionCell";
import CommunityCell from "./CommunityCell";
import { useProfileStore } from "../../../../stores/UserStore";

const ActivationCell = ({ row, value, setTableLoading }) => {
  const { resellerId } = useParams();
  const lang = useLanguage((s) => s.language);
  const [loading, setLoading] = useState(false);
  const profile = useProfileStore((s) => s.profile);

  const level = useMemo(() => {
    return row?.resellers?.find((x) => x.id === parseInt(resellerId))
      ?.user_level;
  }, [resellerId, row.resellers]);

  const toggleUser = () => {
    if (level === "admin") {
      return;
    }
    setLoading(true);
    let func = value === true ? disableUser : enableUserAccount;
    func(row.id)
      .then((response) => {
        notifications.show({
          message: lang.users_list.row_highlight.updated,
          color: "tertiary.8",
        });
        setTableLoading(true);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Group w={"100%"}>
      <Tooltip
        label={
          level === "admin"
            ? lang.users_list.admin_not_allowed
            : value
              ? lang.users_list.deactivate
              : lang.users_list.activate
        }
        refProp="rootRef"
      >
        <Switch
          size="sm"
          defaultChecked={value}
          disabled={level === "admin" || profile?.id === row.id || loading}
          onChange={() => toggleUser()}
          thumbIcon={
            loading ? (
              <Loader type="oval" size={9} />
            ) : !value ? (
              <IconUserX size={11} color="red" />
            ) : (
              <IconUserCheck size={11} color="tertiary.8" />
            )
          }
        />
      </Tooltip>
    </Group>
  );
};

function isUserActivatedBetweenDates(activationDates, startDate, endDate) {
  // Convert startDate and endDate to Date objects if they are not already
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Check if any activation date is within the range
  return activationDates.some((date) => {
    const activationDate = new Date(date);
    return activationDate >= start && activationDate <= end;
  });
}
const dateRangeOperator = [
  {
    label: "Between",
    value: "between",
    getApplyFilterFn: (filterItem, column) => {
      if (!filterItem.field || !filterItem.value || !filterItem.operator) {
        return null;
      }

      return (value, row, column, apiRef) => {
        let activationDates = value.value; //[d1, d2, d3, ...]
        let range = filterItem.value.split(";"); // [d1, d2]

        if (value)
          return isUserActivatedBetweenDates(
            activationDates,
            range[0],
            range[1],
          );
        return false;
      };
    },
  },
];

const CommunityUsersList = ({ loading, setLoading, profile, setProfile }) => {
  const lang = useLanguage((s) => s.language);

  const { resellerId, teamId, orgId } = useParams();

  const apiRef = useGridApiRef();
  const [rows, setRows] = useState([]);

  const columns = useMemo(() => {
    return [
      numberColumn(),
      {
        field: "activated",
        headerName: lang.users_list.columns.activated,
        width: 75,
        valueGetter: ({ value }) => {
          return value ? "active" : "not_active";
        },
        type: "options",
        disabledCondition: true,
        options: [
          {
            label: lang.white_labels.filter_active,
            value: "active",
          },
          {
            label: lang.white_labels.filter_not_active,
            value: "not_active",
          },
        ],
        renderCell: ({ row, value }) => {
          return (
            <ActivationCell
              row={row}
              setTableLoading={setLoading}
              value={value === "active" ? true : false}
            />
          );
        },
      },
      nameColumn({
        field: "first_name",
        headerName: lang.users_list.columns.first_name,
        width: 100,
      }),
      {
        field: "last_name",
        headerName: lang.users_list.columns.last_name,
        width: 130,
      },
      {
        field: "email",
        headerName: lang.users_list.columns.email,
        minWidth: 150,
      },
      {
        field: "mobile_phone",
        headerName: lang.profile.mobile_phone,
        minWidth: 160,
        renderCell: ({ value }) => formatPhone(value),
      },
      {
        field: "level",
        headerName: lang.users_list.columns.level,
        minWidth: 150,
        type: "options",
        disabledCondition: true,
        valueGetter: ({ row }) => {
          let field = resellerId
            ? "resellers"
            : orgId
              ? "organizations"
              : "teams";
          let level = row[field]?.find(
            (x) => x.id === parseInt(resellerId || orgId || teamId),
          )?.user_level;
          return level ? lang.teams.columns["user_level_value_" + level] : null;
        },
        options: [
          {
            label: lang.teams.columns.user_level_value_user,
            value: lang.teams.columns.user_level_value_user,
          },
          {
            label: lang.teams.columns.user_level_value_admin,
            value: lang.teams.columns.user_level_value_admin,
          },
        ],
      },
      {
        field: "subscriptions",
        headerName: lang.users_list.columns.subscription,
        width: 160,
        type: "options",
        disabledCondition: true,
        valueGetter: ({ value }) => {
          return value?.active ? "active" : "not_active";
        },
        options: [
          {
            label: lang.referral.active,
            value: "active",
          },
          {
            label: lang.referral.not_active,
            value: "not_active",
          },
        ],
        renderCell: ({ row }) => (
          <UserSubscriptionCell row={row} apiRef={apiRef} />
        ),
      },
      {
        field: "linkedin_accounts",
        headerName: "LinkedIn",
        minWidth: 150,
        cellClassName: "no-padding",
        headerAlign: "left",
        type: "number",
        valueGetter: ({ row, value }) => {
          return row?.permissions?.find(
            (x) => x.name === "maxIntegrationsLinkedin",
          )?.value;
        },
        renderCell: ({ row }) => (
          <ResellerPointInput
            row={row}
            profile={profile}
            setProfile={setProfile}
            permission="maxIntegrationsLinkedin"
          />
        ),
      },
      {
        field: "email_accounts",
        headerName: "Emails",
        minWidth: 150,
        cellClassName: "no-padding",
        headerAlign: "left",
        type: "number",
        valueGetter: ({ row, value }) => {
          return row?.permissions?.find(
            (x) => x.name === "maxIntegrationsEmailSMTP",
          )?.value;
        },
        renderCell: ({ row }) => (
          <ResellerPointInput
            row={row}
            profile={profile}
            setProfile={setProfile}
            permission="maxIntegrationsEmailSMTP"
          />
        ),
      },
      {
        field: "enrich_credit",
        headerName: lang.white_labels.enrich,
        minWidth: 150,
        cellClassName: "no-padding",
        headerAlign: "left",
        valueGetter: ({ row, value }) => {
          return row?.permissions?.find(
            (x) => x.name === "maxEmailsGeneratedPerMonth",
          )?.value;
        },
        renderCell: ({ row }) => (
          <ResellerPointInput
            row={row}
            profile={profile}
            setProfile={setProfile}
            permission="maxEmailsGeneratedPerMonth"
          />
        ),
      },
      {
        field: "mobile_credit",
        headerName: lang.white_labels.mobile_phone,
        minWidth: 150,
        cellClassName: "no-padding",
        headerAlign: "left",
        valueGetter: ({ row, value }) => {
          return row?.permissions?.find(
            (x) => x.name === "maxMobilePhonesGeneratedPerMonth",
          )?.value;
        },
        renderCell: ({ row }) => (
          <ResellerPointInput
            row={row}
            profile={profile}
            setProfile={setProfile}
            permission="maxMobilePhonesGeneratedPerMonth"
          />
        ),
      },
      tagsColumn({
        field: "reseller_user_tags",
        apiRef,
        filterable: true,
        editFunction: (row) => {
          return ({ id, tags_ids }) =>
            updateUserResellerTags({
              reseller_id: resellerId,
              user_id: id,
              tags_ids,
            });
        },
      }),
      {
        field: "teams",
        headerName: lang.teams.browser_tab_title,
        width: 175,
        valueGetter: (params) => {
          if (params.value) return params.value.map((e) => e.name).join(", ");
          return null;
        },
        renderCell: ({ row }) => {
          return (
            <CommunityCell
              value={row["teams"]}
              apiRef={apiRef}
              row={row}
              tableLoading={loading}
              type={"teams"}
            />
          );
        },
      },
      {
        field: "organizations",
        headerName: lang.organizations.browser_tab_title,
        width: 175,
        valueGetter: (params) => {
          if (params.value) return params.value.map((e) => e.name).join(", ");
          return null;
        },
        renderCell: ({ row }) => {
          return (
            <CommunityCell
              value={row["organizations"]}
              apiRef={apiRef}
              row={row}
              tableLoading={loading}
              type={"organizations"}
            />
          );
        },
      },
      {
        field: "activation_dates",
        headerName: lang.users_list.columns.activation_dates,
        type: "date_range",
        width: 175,
        filterOperators: dateRangeOperator,
        renderCell: ({ value }) => {
          let v = value?.length ? new Date(value[value.length - 1]) : null;

          return v ? parseDate(v) : "-";
        },
      },
      {
        field: "deactivation_dates",
        headerName: lang.users_list.columns.deactivation_dates,
        type: "date_range",
        width: 175,
        filterOperators: dateRangeOperator,
        renderCell: ({ value }) => {
          let v = value?.length ? new Date(value[value.length - 1]) : null;

          return v ? parseDate(v) : "-";
        },
      },
    ];
  }, [loading]);

  const actionsCell = useCallback(
    ({ row }) => {
      return (
        <Group wrap="nowrap" gap={8}>
          <Tooltip
            label={
              !row.activated
                ? lang.errors.main.user_account_disabled
                : lang.global.edit
            }
          >
            <ActionIcon
              component={!row.activated ? undefined : Link}
              to={`/settings/permissions/${row.id}`}
              variant="subtle"
              disabled={!row.activated}
            >
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip>

          <DeleteAccount
            profile={row}
            onSuccess={() => setLoading(true)}
            inTable
          />
        </Group>
      );
    },
    [lang.errors.main.user_account_disabled, lang.global.edit],
  );

  useListingLoader({
    loading,
    setLoading,
    listGetter: () =>
      resellerId
        ? getResellerUsers({
            reseller_id: parseInt(resellerId),
            include_subscriptions: true,
            include_resellers: true,
            // include_teams: true,
            // include_organizations: true,
          })
        : orgId
          ? getOrgUsers(parseInt(orgId))
          : getTeamUsers(parseInt(teamId)),
    responseField: "users_list",
    setRows,
  });

  return (
    <Flex
      h={
        resellerId
          ? "calc(100vh - 230px)"
          : teamId
            ? "calc(100vh - 235px)"
            : "calc(100vh - 250px)"
      }
    >
      <MainTable
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        loading={loading}
        setLoading={setLoading}
        id={"users-table"}
        actionsCell={actionsCell}
        pageOrderName={
          resellerId
            ? "resellers_order"
            : teamId
              ? "teams_order"
              : "organizations_order"
        }
        pageName={resellerId ? "users" : undefined}
        actionsCellWidth={75}
        whiteButtons
      />
    </Flex>
  );
};

export default CommunityUsersList;
